/**
 * PhaseGanttModal — PP pack PP4.
 *
 * Vertical phase Gantt visualisation. Each phase = a horizontal bar
 * positioned by its targetInServiceDate within the project window.
 * Hover/focus shows label, capacity, power source.
 *
 * Time axis: spans from the earliest phase COD to the latest phase COD
 * (+ 1 year of padding). Phases with null COD render at the right edge
 * with a "TBD" badge.
 *
 * Pure presentational — no Firestore writes. Modal pattern: backdrop
 * click + Esc close. No external chart lib — SVG keeps it dependency-
 * free and printable.
 */
import React, { useEffect, useMemo } from 'react';
import type { ProjectIntent } from '../../types/siteRag';
import { POWER_SOURCE_LABEL } from '../../types/siteRag';
import { POWER_BUCKET_LABEL, effectivePhases, phaseIncrementMW } from '../../lib/projectIntent';

export interface PhaseGanttModalProps {
  intent: ProjectIntent | undefined | null;
  open: boolean;
  onClose: () => void;
  testIdPrefix?: string;
}

/** Parse an ISO YYYY-MM-DD-ish date safely. Returns NaN on failure. */
function toMs(iso: string | null | undefined): number {
  if (!iso) return NaN;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : NaN;
}

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

export const PhaseGanttModal: React.FC<PhaseGanttModalProps> = ({
  intent, open, onClose, testIdPrefix = 'phase-gantt',
}) => {
  // Esc-to-close — only when open so we don't capture global Esc.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const view = useMemo(() => effectivePhases(intent), [intent]);

  const { startMs, endMs, ticks } = useMemo(() => {
    const dates = view.phases.map(p => toMs(p.targetInServiceDate)).filter(d => Number.isFinite(d));
    if (dates.length === 0) {
      // No dates — return a 5-year default window starting today.
      const today = Date.now();
      const end = today + 5 * MS_PER_YEAR;
      return { startMs: today, endMs: end, ticks: yearTicks(today, end) };
    }
    const min = Math.min(...dates);
    const max = Math.max(...dates);
    // Pad each side by 6 months so bars don't touch the edges; ensure at
    // least a 2-year span so a single-phase chart still reads as a chart.
    const padStart = min - 0.5 * MS_PER_YEAR;
    const padEnd = Math.max(max + 0.5 * MS_PER_YEAR, padStart + 2 * MS_PER_YEAR);
    return { startMs: padStart, endMs: padEnd, ticks: yearTicks(padStart, padEnd) };
  }, [view.phases]);

  if (!open) return null;

  const totalMs = Math.max(endMs - startMs, 1);
  const rowHeight = 36;
  const chartHeight = view.phases.length * rowHeight + 40;
  const chartWidth = 720;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Phase timeline"
      data-testid={testIdPrefix}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-canvas border border-border max-w-4xl w-full p-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[13px] uppercase tracking-widest text-text-primary font-bold">
            Phase timeline
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid={`${testIdPrefix}-close`}
            className="px-2 py-1 text-[11px] border border-border-subtle text-text-secondary hover:text-text-primary"
          >✕</button>
        </div>

        <div className="text-[11px] text-text-secondary mb-3" data-testid={`${testIdPrefix}-summary`}>
          {view.phaseCount} phase{view.phaseCount === 1 ? '' : 's'} ·{' '}
          {view.totalCapacityMW != null
            ? `${view.totalCapacityMW} MW ultimate`
            : view.ultimateCapacityBucket != null
              ? `${POWER_BUCKET_LABEL[view.ultimateCapacityBucket]} ultimate (range)`
              : view.knownCapacityFloorMW != null
                ? `ultimate capacity TBD (${view.knownCapacityFloorMW} MW known floor)`
                : 'capacity TBD'} ·{' '}
          {view.techMixSummary}
        </div>

        <svg
          width={chartWidth}
          height={chartHeight}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          role="img"
          aria-label="Phase Gantt chart"
          data-testid={`${testIdPrefix}-svg`}
        >
          {/* Year tick labels + grid lines */}
          {ticks.map(t => {
            const x = ((t.ms - startMs) / totalMs) * chartWidth;
            return (
              <g key={t.year}>
                <line
                  x1={x} x2={x} y1={20} y2={chartHeight - 4}
                  stroke="currentColor"
                  className="text-border-subtle"
                  strokeWidth={1}
                  opacity={0.3}
                />
                <text
                  x={x} y={14}
                  textAnchor="middle"
                  className="fill-current text-[10px] text-text-muted"
                >{t.year}</text>
              </g>
            );
          })}

          {/* Phase bars */}
          {view.phases.map((p, i) => {
            const y = 28 + i * rowHeight;
            const phaseMs = toMs(p.targetInServiceDate);
            const hasDate = Number.isFinite(phaseMs);
            const codX = hasDate ? ((phaseMs - startMs) / totalMs) * chartWidth : chartWidth - 110;
            // Bar runs from project start (or last phase COD) to this phase COD.
            // For visual clarity we run from previous-phase-COD to this-COD.
            // Cap minimum width so a same-date adjacent phase still shows up.
            const prevPhaseMs = i > 0 ? toMs(view.phases[i - 1].targetInServiceDate) : NaN;
            const barStartMs = Number.isFinite(prevPhaseMs) ? prevPhaseMs : startMs;
            const barStartX = hasDate ? ((barStartMs - startMs) / totalMs) * chartWidth : codX - 100;
            const barWidth = Math.max(codX - barStartX, 40);
            return (
              <g key={p.id} data-testid={`${testIdPrefix}-bar-${i}`}>
                <rect
                  x={barStartX}
                  y={y}
                  width={barWidth}
                  height={rowHeight - 10}
                  className="fill-primary"
                  opacity={0.85}
                  rx={2}
                />
                <text
                  x={barStartX + 6}
                  y={y + (rowHeight - 10) / 2 + 4}
                  className="fill-canvas text-[10px] font-bold"
                >
                  P{p.sequence} · {(() => {
                    if (p.capacityMW != null) {
                      // Cumulative-at-phase figure + Δ increment (e.g. "200 MW (+100)").
                      // Δ only when the PRIOR phase carries an exact cumulative —
                      // `phaseIncrementMW` treats a null prior as 0, and no honest
                      // increment exists against a range/TBD prior phase.
                      const prevRaw = i > 0 ? view.phases[i - 1].capacityMW : null;
                      const prevExact = i === 0 || (prevRaw != null && Number.isFinite(prevRaw));
                      const incr = prevExact ? phaseIncrementMW(view.phases, i) : null;
                      return `${p.capacityMW} MW${incr != null ? ` (+${incr})` : ''}`;
                    }
                    if (p.capacityBucket != null && p.capacityBucket !== 'undecided') {
                      return `${POWER_BUCKET_LABEL[p.capacityBucket]} (range)`;
                    }
                    return 'MW TBD';
                  })()} · {POWER_SOURCE_LABEL[p.powerSource]}
                </text>
                {!hasDate && (
                  <text
                    x={chartWidth - 4}
                    y={y + (rowHeight - 10) / 2 + 4}
                    textAnchor="end"
                    className="fill-current text-[10px] text-text-muted"
                  >TBD</text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

interface YearTick { year: number; ms: number; }
function yearTicks(startMs: number, endMs: number): YearTick[] {
  const startYear = new Date(startMs).getUTCFullYear();
  const endYear = new Date(endMs).getUTCFullYear();
  const ticks: YearTick[] = [];
  for (let y = startYear; y <= endYear; y += 1) {
    ticks.push({ year: y, ms: Date.UTC(y, 0, 1) });
  }
  return ticks;
}
